﻿/*
* Purpose: Manage Legal Guardian form state and overall validation.
* Boundary: Custom hook only; owns all state and validation logic; no UI.
* Invariants:
* - Validity requires: valid ID, age >= 18, names filled, valid cell, valid OTP, and TCs checked.
* - ASCII only; no Unicode characters in comments or strings.
*/

// Imports
import { useState, useEffect, useMemo } from 'react';

import { useIDValidation } from '../authform/authform-idnumber-hooks';

// Fallback constant (used if server fetch fails)
const TC_GUARDIAN_FALLBACK =
    'I, the parent/legal guardian of the registering minor, confirm and accept this digital Pledge on their behalf: I consent to the submission of their personal information and documents for secure digital certification by Oaths.co.za. I understand this information will be processed in full compliance with POPIA, and that all certifications are subject to legal audit, fraud prevention, and identity verification by Oaths.co.za.';

// Hook useLegalGuardian:
// Manages form state, sub-component values, and derived validity
export function useLegalGuardian({ auth, onChange = () => { } }) {
    // Tracking consent state for guardian: null = not yet chosen, true = accepted, false = declined
    const [guardianTrackingConsentChoice, setGuardianTrackingConsentChoice] = useState(null);

    // Server-provided guardian consent text
    const [guardianTcText, setGuardianTcText] = useState(TC_GUARDIAN_FALLBACK);
    const [guardianTrackingConsentText, setGuardianTrackingConsentText] = useState('');
    useEffect(() => {
        if (!auth?.getRegistrationAgreementText) return;
        auth.getRegistrationAgreementText()
            .then(res => {
                if (res?.data?.tcsGuardian) setGuardianTcText(res.data.tcsGuardian);
                if (res?.data?.trackingConsentMinor) setGuardianTrackingConsentText(res.data.trackingConsentMinor);
            })
            .catch(() => { /* keep fallback */ });
    }, [auth]);

    // Grouped state: Form fields, phone, OTP, and checkboxes
    const [form, setForm] = useState({
        idNumber: '',
        firstName: '',
        lastName: '',
        isValidOTP: false,
        tcAccepted: false,
    });
    const [cellNumber, setCellNumber] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [otpState, setOtpState] = useState({ isValid: false });
    const [checkboxesState, setCheckboxesState] = useState({ checked: [], isValid: false });

    // Derived: ID validation and age
    const { isValidID, age } = useIDValidation(form.idNumber);

    // Handlers: Update form fields
    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    // Effect: Sync OTP validity to form
    useEffect(() => {
        setForm((prev) => ({ ...prev, isValidOTP: otpState.isValid }));
    }, [otpState.isValid]);

    // Effect: Sync TCs validity to form
    useEffect(() => {
        setForm((prev) => ({ ...prev, tcAccepted: checkboxesState.isValid }));
    }, [checkboxesState.isValid]);

    // Derived: OTP readiness gate (everything valid except OTP itself)
    const isProceedReadyOtp = useMemo(() =>
        isValidID &&
        age >= 18 &&
        !!form.firstName.trim() &&
        !!form.lastName.trim() &&
        !!cellNumber &&
        isPhoneValid &&
        form.tcAccepted &&
        guardianTrackingConsentChoice !== null,
        [isValidID, age, form.firstName, form.lastName, cellNumber, isPhoneValid, form.tcAccepted, guardianTrackingConsentChoice]);

    // Derived: Overall validity (memoized for reactivity)
    const isValid = useMemo(() =>
        isProceedReadyOtp &&
        form.isValidOTP,
        [isProceedReadyOtp, form.isValidOTP]);

    // Effect: Push DTO changes to parent via onChange
    useEffect(() => {
        onChange({ isValid, ...form, cellNumber, age, guardianTrackingConsentChoice });
    }, [isValid, form, cellNumber, age, guardianTrackingConsentChoice, onChange]);

    // Return: States, setters, and derived values
    return {
        form,
        setField,
        cellNumber,
        setCellNumber,
        isPhoneValid,
        setIsPhoneValid,
        otpState,
        setOtpState,
        checkboxesState,
        setCheckboxesState,
        age,
        isProceedReadyOtp,
        isValid,
        auth,
        guardianTcText,
        guardianTrackingConsentText,
        guardianTrackingConsentChoice,
        setGuardianTrackingConsentChoice,
    };
}